// Araç Yükleme Onay Paneli — master-detail'in SAĞ tarafı (spec §6.3).
//
// Neden ayrı sayfa değil: onaycı günde 100+ talep bakıyor, her talep için sayfa geçmek maliyetli.
// Panel liste seçimiyle senkron çalışır; onay/red sonrası listeyi tazeler (onChanged).
//
// AKSİYONLAR (spec §6.4 — örnek ekrandaki 8 butonun 6'sı karşılıksızdı; burada seçime göre
// AKTİFLEŞEN 4 aksiyon + 2 yardımcı vardır):
//   Onayla · Revize Ederek Onayla · Reddet (gerekçe ZORUNLU) · İptal Et
//   yardımcı: Excel'e Aktar · SAP'ye Tekrar Gönder
import { useState, useEffect, useCallback, useMemo } from 'react';
import { authHeaders, exportToExcel } from '../listPageStyles';
import { WarningBand } from './WarningBand';
import { Plasiyer360Panel } from './Plasiyer360Panel';
import {
  API_BASE, pick, unwrap, statusBadge, formatDateTime, formatDate, formatMoney, formatNumber,
  requestedQtyOf, isClosedStatus, isApprovedStatus, isReceivedStatus,
  REQUEST_TYPE_META, REVISION_REASONS, waitingInfo, formatDuration,
} from './vehicleLoadShared';
import './vehicleLoad.css';

const AUDIT_ACTION_LABEL = {
  approved: 'onayladı',
  revised: 'revize ederek onayladı',
  rejected: 'reddetti',
  cancelled: 'iptal etti',
  received: 'teslim alındı',
};

export function VehicleLoadApprovalPanel({ loadId, canEdit = true, onChanged, showToast }) {
  const [data, setData] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [cutPct, setCutPct] = useState('10');
  const [deviationPct, setDeviationPct] = useState(null);

  const load = useCallback(async () => {
    if (!loadId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/${loadId}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Talep alınamadı (${res.status})`);
      const payload = unwrap(await res.json()) || {};
      setData(payload);
      const items = payload.items || payload.Items || [];
      setLines(items.map((l) => {
        const requested = requestedQtyOf(l);
        const approved = pick(l, 'approvedQuantity', 'ApprovedQuantity');
        return {
          id: pick(l, 'id', 'Id'),
          productId: pick(l, 'productId', 'ProductId') || null,
          productCode: pick(l, 'productCode', 'ProductCode') || '',
          productName: pick(l, 'productName', 'ProductName') || '-',
          unit: pick(l, 'unit', 'Unit') || '',
          requested,
          // Onay öncesi varsayılan = talep edilen (onaycı yalnız değiştirmek istediğine dokunur).
          approved: approved == null ? requested : Number(approved),
          revisionReason: pick(l, 'revisionReason', 'RevisionReason') || '',
          isRejected: Boolean(pick(l, 'isRejected', 'IsRejected')),
          unitPrice: Number(pick(l, 'unitPrice', 'UnitPrice') || 0),
          selected: false,
        };
      }));
    } catch (err) {
      showToast?.('error', err.message || 'Talep alınamadı');
      setData(null);
      setLines([]);
    } finally {
      setLoading(false);
    }
  }, [loadId, showToast]);

  useEffect(() => { load(); }, [load]);

  const header = data?.header || data?.Header || null;
  const approvals = data?.approvals || data?.Approvals || [];
  const warnings = data?.warnings || data?.Warnings || [];
  const status = pick(header, 'status', 'Status');

  const closed = isClosedStatus(status);
  const approved = isApprovedStatus(status);
  const received = isReceivedStatus(status);

  // Satır bazında değişiklik var mı? → "Revize Ederek Onayla" YALNIZ o zaman aktifleşir.
  const dirty = useMemo(
    () => lines.some((l) => l.isRejected || Number(l.approved) !== Number(l.requested)),
    [lines],
  );
  // Onaylanan > talep edilen OLABİLİR ama özel yetki ister → uyarı (spec §9.7).
  const overApprovedCount = useMemo(
    () => lines.filter((l) => !l.isRejected && Number(l.approved) > Number(l.requested)).length,
    [lines],
  );

  const setLine = (id, patch) =>
    setLines((cur) => cur.map((l) => (l.id === id ? { ...l, ...patch } : l)));

  const selectedCount = lines.filter((l) => l.selected).length;

  /** Toplu revize: "seçili ürünlerde %X kes" (spec §6.3). */
  const applyBulkCut = () => {
    const pct = Number(cutPct);
    if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
      showToast?.('error', 'Kesinti oranı 1–100 arasında olmalı');
      return;
    }
    setLines((cur) => cur.map((l) => (l.selected
      ? { ...l, approved: Math.max(0, Math.round(Number(l.requested) * (1 - pct / 100))), revisionReason: l.revisionReason || 'Stok yetersiz' }
      : l)));
    showToast?.('info', `${selectedCount} kalemde %${pct} kesildi`);
  };

  const post = async (path, body) => {
    const res = await fetch(`${API_BASE}/${loadId}/${path}`, {
      method: 'POST',
      headers: authHeaders(),
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const payload = await res.json().catch(() => null);
    if (!res.ok) throw new Error(pick(payload, 'Message', 'message') || `İşlem başarısız (${res.status})`);
    return unwrap(payload) || {};
  };

  const runAction = async (fn, okMessage) => {
    setBusy(true);
    try {
      await fn();
      showToast?.('success', okMessage);
      await load();
      onChanged?.();
      return true;
    } catch (err) {
      showToast?.('error', err.message || 'İşlem başarısız');
      return false;
    } finally {
      setBusy(false);
    }
  };

  // Onayla: satırlara dokunulmadıysa items GÖNDERİLMEZ → backend talep edileni aynen onaylar.
  const handleApprove = () => runAction(() => post('approve', {}), 'Talep onaylandı');

  // Revize Ederek Onayla: satır bazlı onaylanan miktar + gerekçe + satır reddi gider.
  const handleReviseApprove = () => runAction(
    () => post('approve', {
      items: lines.map((l) => ({
        lineId: l.id,
        approvedQty: Number(l.approved) || 0,
        revisionReason: l.revisionReason || null,
        isRejected: Boolean(l.isRejected),
      })),
    }),
    'Talep revize edilerek onaylandı',
  );

  // Reddet: GEREKÇE ZORUNLU — boş gönderilirse backend 400 döner.
  const handleReject = async () => {
    const reason = rejectReason.trim();
    if (!reason) { showToast?.('error', 'Red gerekçesi zorunludur'); return; }
    const ok = await runAction(() => post('reject', { reason }), 'Talep reddedildi');
    if (ok) { setRejectOpen(false); setRejectReason(''); }
  };

  const handleCancel = () => runAction(() => post('cancel', {}), 'Talep iptal edildi');
  const handleResendSap = () => runAction(async () => {
    const res = await post('resend-sap', {});
    if (!res.sent && !res.Sent) throw new Error('SAP entegrasyonu yanıt vermedi (SAP no atanmadı).');
  }, 'SAP’ye tekrar gönderildi');

  const handleExport = () => {
    if (lines.length === 0) { showToast?.('error', 'Dışa aktarılacak kalem yok'); return; }
    exportToExcel(lines, [
      { field: 'productCode', headerName: 'Ürün Kodu' },
      { field: 'productName', headerName: 'Ürün' },
      { field: 'unit', headerName: 'Birim' },
      { field: 'requested', headerName: 'Talep' },
      { field: 'approved', headerName: 'Onaylanan' },
      { headerName: 'Fark', valueGetter: ({ row }) => Number(row.approved) - Number(row.requested) },
      { field: 'unitPrice', headerName: 'Birim Fiyat' },
      { headerName: 'Tutar', valueGetter: ({ row }) => Number(row.approved) * Number(row.unitPrice) },
      { field: 'revisionReason', headerName: 'Revize Nedeni' },
    ], `arac-yukleme_${pick(header, 'requestNo', 'RequestNo') || loadId}`);
    showToast?.('success', `${lines.length} kalem dışa aktarıldı`);
  };

  if (loading) return <div className="vl-card vl-empty">Talep yükleniyor…</div>;
  if (!header) return <div className="vl-card vl-empty">Talep bulunamadı.</div>;

  const wait = waitingInfo(header);
  const extraWarnings = [];
  if (deviationPct != null && Math.abs(deviationPct) >= 30) {
    extraWarnings.push(`Talep, son yüklemelerin ortalamasından %${Math.abs(deviationPct)} ${deviationPct > 0 ? 'yüksek' : 'düşük'}.`);
  }
  if (overApprovedCount > 0) {
    extraWarnings.push(`${overApprovedCount} kalemde onaylanan miktar talep edileni AŞIYOR (özel yetki gerektirir).`);
  }

  const approvedTotal = lines.reduce(
    (sum, l) => sum + (l.isRejected ? 0 : Number(l.approved) * Number(l.unitPrice)), 0,
  );

  return (
    <div className="vl-panel">
      <WarningBand warnings={warnings} extra={extraWarnings} />

      {/* ── Üst: başlık + durum zaman çizelgesi ── */}
      <div className="vl-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }} className="vl-mono">
              {pick(header, 'requestNo', 'RequestNo') || '(talep no yok)'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary, #64748b)', marginTop: 2 }}>
              {pick(header, 'plate', 'Plate') || '—'} · {pick(header, 'sdmName', 'SdmName') || '—'}
            </div>
          </div>
          {statusBadge(status)}
        </div>

        <div style={{ height: 8 }} />
        <div className="vl-kv"><span>Yükleme tarihi</span><strong>{formatDate(pick(header, 'shipmentDate', 'ShipmentDate') || pick(header, 'deliveryDate', 'DeliveryDate'))}</strong></div>
        <div className="vl-kv"><span>Talep tipi</span><span>{REQUEST_TYPE_META[pick(header, 'requestType', 'RequestType')] || pick(header, 'requestType', 'RequestType') || 'Normal'}</span></div>
        <div className="vl-kv">
          <span>Toplam</span>
          <strong>
            {formatNumber(pick(header, 'distinctSku', 'DistinctSku') ?? lines.length)} kalem ·{' '}
            {formatNumber(pick(header, 'totalCases', 'TotalCases'))} koli ·{' '}
            {formatNumber(pick(header, 'totalWeightKg', 'TotalWeightKg'), 1)} kg
          </strong>
        </div>
        <div className="vl-kv">
          <span>Kapasite</span>
          <strong className={Number(pick(header, 'capacityUsagePct', 'CapacityUsagePct')) > 100 ? 'vl-cap--over' : undefined}>
            %{formatNumber(pick(header, 'capacityUsagePct', 'CapacityUsagePct'), 1)}
          </strong>
        </div>
        <div className="vl-kv"><span>Tutar (KDV dahil)</span><strong>{formatMoney(pick(header, 'netAmount', 'NetAmount'))}</strong></div>
        {pick(header, 'sapOrderNo', 'SapOrderNo') && (
          <div className="vl-kv"><span>SAP sipariş no</span><span className="vl-mono">{pick(header, 'sapOrderNo', 'SapOrderNo')}</span></div>
        )}
        {pick(header, 'purchaseInvoiceNo', 'PurchaseInvoiceNo') && (
          <div className="vl-kv"><span>Alış fatura no</span><span className="vl-mono">{pick(header, 'purchaseInvoiceNo', 'PurchaseInvoiceNo')}</span></div>
        )}
        {pick(header, 'notes', 'Notes') && (
          <div className="vl-kv"><span>Plasiyer notu</span><span style={{ fontStyle: 'italic' }}>{pick(header, 'notes', 'Notes')}</span></div>
        )}

        <p className="vl-card-title" style={{ marginTop: 12 }}>Durum Zaman Çizelgesi</p>
        <ul className="vl-timeline">
          <li>
            <div>Talep gönderildi</div>
            <div className="vl-tl-when">{formatDateTime(pick(header, 'sentAt', 'SentAt') || pick(header, 'createdDate', 'CreatedDate'))}</div>
          </li>
          {approvals.map((a) => (
            <li key={pick(a, 'id', 'Id')}>
              <div>
                <strong>{pick(a, 'approverName', 'ApproverName') || 'sistem'}</strong>{' '}
                {AUDIT_ACTION_LABEL[pick(a, 'action', 'Action')] || pick(a, 'action', 'Action')}
              </div>
              <div className="vl-tl-when">{formatDateTime(pick(a, 'actedAt', 'ActedAt'))}</div>
              {pick(a, 'reason', 'Reason') && <div className="vl-tl-reason">“{pick(a, 'reason', 'Reason')}”</div>}
            </li>
          ))}
          {!closed && !approved && (
            <li>
              <div>Onay bekleniyor</div>
              <div className={wait.tone === 'crit' ? 'vl-wait--crit' : wait.tone === 'warn' ? 'vl-wait--warn' : 'vl-tl-when'}>
                {formatDuration(wait.elapsedMin)}
                {wait.remainingMin != null && (
                  wait.remainingMin < 0
                    ? ' · 04:00 deadline’ı GEÇTİ'
                    : ` · 04:00’a ${formatDuration(wait.remainingMin)} kaldı`
                )}
              </div>
            </li>
          )}
        </ul>
      </div>

      {/* ── Orta: kalem tablosu (inline onay miktarı revizesi) ── */}
      <div className="vl-card">
        <p className="vl-card-title">Kalemler · onaylanan miktarı düzenleyebilirsiniz</p>

        {canEdit && !closed && !approved && (
          <div className="vl-bulk-revise">
            <strong>Toplu revize:</strong>
            <span>seçili {selectedCount} üründe</span>
            <input
              type="number" min="1" max="100" value={cutPct}
              onChange={(e) => setCutPct(e.target.value)}
              style={{ width: 54 }} className="vl-qty"
              aria-label="Kesinti yüzdesi"
            />
            <span>% kes</span>
            <button type="button" className="vl-chip" disabled={selectedCount === 0} onClick={applyBulkCut}>Uygula</button>
            <button
              type="button" className="vl-chip"
              onClick={() => setLines((cur) => cur.map((l) => ({ ...l, approved: l.requested, revisionReason: '', isRejected: false, selected: false })))}
            >
              Sıfırla
            </button>
          </div>
        )}

        <table className="vl-lines">
          <thead>
            <tr>
              {canEdit && !closed && !approved && (
                <th style={{ width: 22 }}>
                  <input
                    type="checkbox"
                    checked={lines.length > 0 && selectedCount === lines.length}
                    onChange={(e) => setLines((cur) => cur.map((l) => ({ ...l, selected: e.target.checked })))}
                    aria-label="Tüm kalemleri seç"
                  />
                </th>
              )}
              <th>Ürün</th>
              <th className="num">Talep</th>
              <th className="num">Onaylanan</th>
              <th className="num">Fark</th>
              <th className="num">Tutar</th>
              <th>Neden</th>
              {canEdit && !closed && !approved && <th style={{ width: 30 }} />}
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 && (
              <tr><td colSpan={8} style={{ color: '#64748b' }}>Kalem yok</td></tr>
            )}
            {lines.map((l) => {
              const diff = Number(l.approved) - Number(l.requested);
              const editable = canEdit && !closed && !approved;
              return (
                <tr key={l.id} className={l.isRejected ? 'is-rejected' : undefined}>
                  {editable && (
                    <td>
                      <input
                        type="checkbox" checked={l.selected}
                        onChange={(e) => setLine(l.id, { selected: e.target.checked })}
                        aria-label={`${l.productName} seç`}
                      />
                    </td>
                  )}
                  <td>
                    {l.productName}
                    {l.productCode && <div style={{ fontSize: 10, color: '#94a3b8' }}>{l.productCode}</div>}
                  </td>
                  <td className="num">{formatNumber(l.requested)} {l.unit}</td>
                  <td className="num">
                    {editable ? (
                      <input
                        className="vl-qty" type="number" min="0" value={l.approved}
                        disabled={l.isRejected}
                        onChange={(e) => setLine(l.id, { approved: e.target.value === '' ? 0 : Number(e.target.value) })}
                        aria-label={`${l.productName} onaylanan miktar`}
                      />
                    ) : formatNumber(l.approved)}
                  </td>
                  <td className={`num ${diff < 0 ? 'vl-diff-neg' : diff > 0 ? 'vl-diff-pos' : 'vl-diff-zero'}`}>
                    {diff === 0 ? '0' : `${diff > 0 ? '+' : '−'}${Math.abs(diff)}`}
                  </td>
                  <td className="num">{formatMoney((l.isRejected ? 0 : Number(l.approved)) * Number(l.unitPrice))}</td>
                  <td>
                    {editable && diff !== 0 ? (
                      <select
                        className="vl-reason-sel" value={l.revisionReason}
                        onChange={(e) => setLine(l.id, { revisionReason: e.target.value })}
                        aria-label={`${l.productName} revize nedeni`}
                      >
                        <option value="">Neden seç…</option>
                        {REVISION_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    ) : (l.revisionReason || '—')}
                  </td>
                  {editable && (
                    <td>
                      {/* Satır bazlı reddetme (spec §6.3) */}
                      <button
                        type="button" className="vl-chip"
                        style={{ padding: '2px 6px' }}
                        title={l.isRejected ? 'Reddi geri al' : 'Satırı reddet'}
                        onClick={() => setLine(l.id, { isRejected: !l.isRejected, approved: l.isRejected ? l.requested : 0 })}
                      >
                        {l.isRejected ? '↺' : '✕'}
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        {lines.length > 0 && (
          <div className="vl-kv" style={{ marginTop: 8, borderTop: '1px solid rgba(15,23,42,0.10)', paddingTop: 6 }}>
            <span>Onaylanacak tutar (KDV hariç)</span><strong>{formatMoney(approvedTotal)}</strong>
          </div>
        )}
      </div>

      {/* ── Alt: Plasiyer 360 ── */}
      <Plasiyer360Panel
        loadId={loadId}
        netAmount={Number(pick(header, 'netAmount', 'NetAmount') || 0)}
        requestedLines={lines.map((l) => ({ productId: l.productId, productName: l.productName, requestedQty: l.requested }))}
        onMetrics={({ deviationPct: d }) => setDeviationPct(d)}
      />

      {/* ── Aksiyonlar: SEÇİME GÖRE aktifleşen 4 aksiyon + 2 yardımcı ── */}
      <div className="vl-card">
        <div className="vl-acts">
          <button
            type="button" className="vl-act vl-act--primary"
            disabled={busy || !canEdit || closed || approved || dirty}
            title={dirty ? 'Satırlarda değişiklik var — "Revize Ederek Onayla" kullanın' : 'Talebi olduğu gibi onayla'}
            onClick={handleApprove}
          >
            ✓ Onayla
          </button>
          <button
            type="button" className="vl-act vl-act--warn"
            disabled={busy || !canEdit || closed || approved || !dirty}
            title={dirty ? 'Değiştirilen miktarlarla onayla' : 'Önce bir kalemde miktarı değiştirin'}
            onClick={handleReviseApprove}
          >
            ✎ Revize Ederek Onayla
          </button>
          <button
            type="button" className="vl-act vl-act--danger"
            disabled={busy || !canEdit || closed}
            onClick={() => { setRejectReason(''); setRejectOpen(true); }}
          >
            ✕ Reddet
          </button>
          <button
            type="button" className="vl-act"
            disabled={busy || !canEdit || received || status === 'CANCELLED'}
            onClick={handleCancel}
          >
            ⊘ İptal Et
          </button>
        </div>
        <div className="vl-acts" style={{ marginTop: 6 }}>
          <button type="button" className="vl-act" onClick={handleExport} disabled={busy}>⤓ Excel’e Aktar</button>
          <button
            type="button" className="vl-act"
            disabled={busy || !approved}
            title={approved ? 'Entegrasyon hatasında SAP’ye tekrar gönder' : 'Yalnız onaylanmış talep gönderilebilir'}
            onClick={handleResendSap}
          >
            ⟳ SAP’ye Tekrar Gönder
          </button>
        </div>
      </div>

      {/* Red gerekçesi ZORUNLU (§6.4) — boşsa buton kapalı, backend de 400 döner. */}
      {rejectOpen && (
        <div className="vl-card" style={{ borderColor: '#dc2626' }}>
          <p className="vl-card-title" style={{ color: '#dc2626' }}>Red Gerekçesi (zorunlu)</p>
          <textarea
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Örn: Kredi limiti aşıldı, ödeme sonrası tekrar talep ediniz."
            style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid rgba(15,23,42,0.18)', fontSize: 12 }}
          />
          <div className="vl-acts" style={{ marginTop: 8 }}>
            <button type="button" className="vl-act vl-act--danger" disabled={busy || !rejectReason.trim()} onClick={handleReject}>
              {busy ? 'İşleniyor…' : 'Reddet'}
            </button>
            <button type="button" className="vl-act" disabled={busy} onClick={() => setRejectOpen(false)}>Vazgeç</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default VehicleLoadApprovalPanel;
